import React, { ReactNode } from 'react';
import { Button, Modal, Stack, Form, Row, Col } from 'react-bootstrap';
import useUserStore from 'store/user';
import {
	useCountryListQuery,
	useIndustriesListQuery,
	useUpdateCompanyDetailsMutation,
} from 'services';
import useTranslate from 'hooks/useTranslate';
import toast from 'react-hot-toast';
import CustomSearchAddress from 'components/Common/CustomSearchAddress';
import { useFormik } from 'formik';
import {
	CompanyPersonalInfoInitialValues,
	companyDetailsSchema,
} from 'constants/schemaValidations';
import { CompanyPersonalInfoInterface } from 'constants/interfaces';
import ReactSelect from 'react-select';
import useAuthStore from 'store/auth';
import useCommonStore, { setModalConfig } from 'store/common';
import { getPhoneFormat } from 'utils';

const EditCompanyDetails: React.FC<any> = () => {
	const { translate } = useTranslate();
	const { profileData }: any = useUserStore((state) => state);
	const { modalConfig = {} }: any = useCommonStore((state) => state);
	const { mutateAsync: updateProfile } = useUpdateCompanyDetailsMutation();
	const { data: countryList = [] } = useCountryListQuery();
	const { data: { industries: industrieslist = [] } = {} } = useIndustriesListQuery();

	const { user }: Record<string, any> = useAuthStore((state) => state.userInfo ?? {});

	const onHandleNumberVerify = () => {
		setModalConfig({
			visible: true,
			type: 'verifyMobileNumber',
		});
	};

	const formik: any = useFormik({
		initialValues: {
			...CompanyPersonalInfoInitialValues,
			companyName: profileData?.companyName || '',
			phoneNumber: profileData?.phoneNumber || '',
			industryId: profileData?.industryId || '',
			websiteLink: profileData?.websiteLink || '',
			cityId: profileData?.city?.id || null,
			stateId: profileData?.state?.id || null,
			countryId: profileData?.country?.id || null,
			address: profileData?.address || '',
			countryCode: profileData?.countryCode || '',
		},
		enableReinitialize: true,
		validationSchema: companyDetailsSchema(translate),
		onSubmit: (values: any) => {
			updateProfile({
				...values,
				saveType: 'companyDetails',
			})
				.then((res: any) => {
					if (res?.success) {
						toast.success(res?.message);
					}
				})
				.finally(() => modalConfig?.onClose());
		},
	});
	const renderError = <T extends keyof CompanyPersonalInfoInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	const handleChange = (e: any) => {
		const inputValue = getPhoneFormat(e.target.value);
		if (inputValue) {
			formik.setFieldValue('phoneNumber', e.target.value);
		}
	};
	return (
		<Modal.Body>
			<Button
				className="close-btn rounded-circle border-0 fw-bold position-absolute"
				onClick={() => modalConfig?.onClose()}
			>
				<i className="ri-close-line"></i>
			</Button>
			<Form className="edit-form" onSubmit={formik.handleSubmit}>
				<Stack className="gap-4">
					<h4 className="fw-bold mb-0">{translate('Profile.editCompanyInfo')}</h4>
					<Row>
						<Col md="12">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Profile.editcompanyName')}</Form.Label>
								<Form.Control
									type="text"
									placeholder={translate('PlaceHolder.enterCompanyName')}
									className="rounded-pill"
									name="companyName"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.companyName}
									autoComplete="off"
								/>
								{renderError('companyName')}
							</Form.Group>
						</Col>
						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Profile.editMobileNumber')}</Form.Label>
								<Button
									variant="link"
									className="border-0 p-0 ms-3 verify-number-link"
									disabled={user?.isPhoneNumberVerified || !user?.phoneNumber}
									onClick={() => (!user?.isPhoneNumberVerified ? onHandleNumberVerify() : '')}
								>
									{user?.isPhoneNumberVerified
										? translate('Common.verified')
										: translate('Common.verifyNumber')}
								</Button>
								<div className="number-input d-flex">
									<ReactSelect
										className="form-react-select"
										classNamePrefix="select"
										isSearchable
										name="countryCode"
										autoFocus={false}
										options={countryList}
										getOptionLabel={(option: any) => option?.phonecode}
										getOptionValue={(option: any) => option?.phonecode}
										onChange={(value: any) => {
											formik.setFieldValue('countryCode', value?.phonecode);
										}}
										value={countryList?.find(
											(item: any) => item.phonecode === formik.values.countryCode,
										)}
										onBlur={formik.handleBlur}
									/>
									<Form.Control
										type="text"
										placeholder={translate('PlaceHolder.phone')}
										name="phoneNumber"
										className="rounded-pill flex-1"
										onChange={handleChange}
										onBlur={formik.handleBlur}
										value={formik.values.phoneNumber}
										autoComplete="off"
										maxLength={12}
									/>
								</div>
								{renderError('countryCode') || renderError('phoneNumber')}
							</Form.Group>
						</Col>
						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Profile.editInduestry')}</Form.Label>
								<ReactSelect
									isSearchable
									name="industryId"
									options={industrieslist}
									getOptionValue={(option) => option.id}
									getOptionLabel={(option) => option.title}
									onChange={(optionValue: any) =>
										formik.setFieldValue('industryId', optionValue?.id)
									}
									onBlur={formik.handleBlur}
									autoFocus={false}
									placeholder={translate('PlaceHolder.selectIndustry')}
									className="form-react-select"
									classNamePrefix="select"
									value={industrieslist?.find((item: any) => item.id === formik.values.industryId)}
								/>
								{renderError('industryId')}
							</Form.Group>
						</Col>
						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Profile.editCompanyUrl')}</Form.Label>
								<Form.Control
									type="text"
									placeholder={translate('PlaceHolder.enterCompanyUrl')}
									className="rounded-pill"
									name="websiteLink"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.websiteLink}
									autoComplete="off"
								/>
								{renderError('websiteLink')}
							</Form.Group>
						</Col>
						<Col md="6">
							<Form.Group className="mb-4 position-relative custom-search-address">
								<Form.Label className="lh-1">{translate('PlaceHolder.location')}</Form.Label>
								<CustomSearchAddress
									selectProps={{
										placeholder: translate('PlaceHolder.locationText'),
										onChange: (value: any) => {
											formik.setFieldValue('address', value.location);
											formik.setFieldValue('cityId', value.cityId);
											formik.setFieldValue('stateId', value.stateId);
											formik.setFieldValue('countryId', value.countryId);
										},
										name: 'address',
									}}
									fieldValue={formik.values.address}
									onBlur={formik.handleBlur}
								/>
								<i className="ri-search-line form-icon position-absolute lh-1"></i>
								{renderError('address') ||
									renderError('cityId') ||
									renderError('stateId') ||
									renderError('countryId')}
							</Form.Group>
						</Col>
					</Row>
					<div className="edit-modal-actions d-flex gap-4">
						<Button className="filled-btn rounded-pill" type="submit">
							{translate('Common.save')}
						</Button>
					</div>
				</Stack>
			</Form>
		</Modal.Body>
	);
};

export default EditCompanyDetails;
